import AI from '@lideralia/alife-uikit/dist/atoms/imgs/AI';
import Briefcase from '@lideralia/alife-uikit/dist/atoms/imgs/Briefcase';
import Camera from '@lideralia/alife-uikit/dist/atoms/imgs/Camera';
import Diamond from '@lideralia/alife-uikit/dist/atoms/imgs/Diamond';
import Happy from '@lideralia/alife-uikit/dist/atoms/imgs/Happy';
import Heart from '@lideralia/alife-uikit/dist/atoms/imgs/Heart';
import Music from '@lideralia/alife-uikit/dist/atoms/imgs/Music';
import Plant from '@lideralia/alife-uikit/dist/atoms/imgs/Plant';
import Sunrise from '@lideralia/alife-uikit/dist/atoms/imgs/Sunrise';
import { Container, Flex } from '@lideralia/alife-uikit/dist/atoms/Layout';
import { Loader } from '@lideralia/alife-uikit/dist/atoms/Loader';
import { Text } from '@lideralia/alife-uikit/dist/atoms/Typography';
import { BlockCard } from '@lideralia/alife-uikit/dist/molecules/BlockCard';
import { EditorProgress } from '@lideralia/alife-uikit/dist/organisms/BiographicalEditor/EditorProgress';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getActualLanguage } from '../../../../common/functions/lang';
import { generateMemory, setCurrentBlock } from '../../actions';
import { BLOCKS_CONFIG, MEMORY_STATUS, REQUIRED_BLOCKS } from '../../config';

const ICON_MAP = {
  plant: <Plant />,
  camera: <Camera />,
  heart: <Heart />,
  sunrise: <Sunrise />,
  happy: <Happy />,
  briefcase: <Briefcase />,
  diamond: <Diamond />,
  music: <Music />,
};

export const BlockIndexScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { idLine, idName } = useParams();
  const lang = getActualLanguage();
  const intl = useIntl();

  const { memory, status } = useSelector((state) => state.biographicalEditor);

  const blocks = memory?.blocks ?? [];
  const completedBlocks = blocks
    .filter((b) => b.is_completed)
    .map((b) => b.block_key);

  const requiredBlocks = REQUIRED_BLOCKS;
  const optionalBlocks = BLOCKS_CONFIG.filter((b) => b.type === 'optional');
  const completedRequired = requiredBlocks.filter((b) => completedBlocks.includes(b.key)).length;
  const allRequiredDone = completedRequired === requiredBlocks.length;
  const progressPercent = Math.round((completedRequired / requiredBlocks.length) * 100);

  const isGenerating = memory?.status === MEMORY_STATUS.GENERATING;
  const isCompleted = memory?.status === MEMORY_STATUS.COMPLETED;

  useEffect(() => {
    if (isGenerating) {
      history.push(`/${lang}/lines/${idLine}/${idName}/biographical-editor/generating`);
    }
    if (isCompleted) {
      history.push(`/${lang}/lines/${idLine}/${idName}/biographical-editor/result`);
    }
  }, [isGenerating, isCompleted]);

  const [footerHeight, setFooterHeight] = useState(0);
  const observerRef = useRef(null);

  const footerRef = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (el) {
      const observer = new ResizeObserver(([entry]) => {
        const { height } = entry.contentRect;
        requestAnimationFrame(() => setFooterHeight(height));
      });
      observer.observe(el);
      observerRef.current = observer;
    }
  }, []);

  const handleBlockClick = (blockKey) => {
    dispatch(setCurrentBlock(blockKey));
    history.push(`/${lang}/lines/${idLine}/${idName}/biographical-editor/blocks/${blockKey}`);
  };

  const handleGenerate = () => {
    if (memory?.uuid) {
      dispatch(generateMemory(idLine, memory.uuid));
    }
  };

  if (status === 'initializing' || status === 'loading') {
    return (
      <Container paddingY={3} maxWidth={['400px']}>
        <Flex justifyContent="center" paddingY={5}>
          <Loader />
        </Flex>
      </Container>
    );
  }

  return (
    <>
    <Container paddingY={3} paddingBottom={8} maxWidth={['400px']}>
      {/* Header */}
      <Flex flexDirection="column" paddingBottom={3}>
        <Text
          as="h1"
          sizeText="display24"
          fontWeight="bold"
          color="black84"
          style={{ marginBottom: 4 }}
        >
          <FormattedMessage id="biographicalEditor.blockIndex.title" />
        </Text>
        <Text sizeText="display13" color="black60">
          <FormattedMessage id="biographicalEditor.blockIndex.subtitle" values={{ br: <br /> }} />
        </Text>
      </Flex>

      {/* Section label */}
      <Text
        sizeText="display12"
        fontWeight="bold"
        color="black60"
        style={{ textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}
      >
        <FormattedMessage id="biographicalEditor.blockIndex.required" />
      </Text>

      <Flex flexDirection="column" gap="9px">
      {/* Block list (required only for FREE) */}
      {requiredBlocks.map((block) => {
        const isCompleted = completedBlocks.includes(block.key);
        return (
          <BlockCard
            key={block.key}
            blockKey={block.key}
            name={intl.formatMessage({ id: `biographicalEditor.blocks.${block.key}.name` })}
            icon={ICON_MAP[block.icon]}
            iconColor={block.iconColor}
            status={isCompleted ? 'completed' : 'pending'}
            statusLabel={intl.formatMessage({
              id: isCompleted
                ? 'biographicalEditor.blockIndex.completed'
                : 'biographicalEditor.blockIndex.pending',
            })}
            onClick={() => handleBlockClick(block.key)}
          />
        );
      })}
      </Flex>

      {/* Optional blocks */}
      {optionalBlocks.length > 0 && (
        <>
          <Text
            sizeText="display12"
            fontWeight="bold"
            color="black60"
            style={{ textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, marginTop: 18 }}
          >
            <FormattedMessage id="biographicalEditor.blockIndex.optional" defaultMessage="Optional" />
          </Text>

          <Flex flexDirection="column" gap="9px">
            {optionalBlocks.map((block) => {
              const isCompleted = completedBlocks.includes(block.key);
              return (
                <BlockCard
                  key={block.key}
                  blockKey={block.key}
                  name={intl.formatMessage({ id: `biographicalEditor.blocks.${block.key}.name` })}
                  icon={ICON_MAP[block.icon]}
                  iconColor={block.iconColor}
                  status={isCompleted ? 'completed' : 'pending'}
                  statusLabel={intl.formatMessage({
                    id: isCompleted
                      ? 'biographicalEditor.blockIndex.completed'
                      : 'biographicalEditor.blockIndex.pending',
                  })}
                  onClick={() => handleBlockClick(block.key)}
                />
              );
            })}
          </Flex>
        </>
      )}

      <div style={{ height: footerHeight }} />
    </Container>

    <div ref={footerRef} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100 }}>
    <EditorProgress
      requiredCompleted={completedRequired}
      requiredTotal={requiredBlocks.length}
      percentage={progressPercent}
      generateLabel={intl.formatMessage({ id: 'biographicalEditor.blockIndex.generate' })}
      generateDisabled={!allRequiredDone}
      generateIcon={<AI color="#ffffff" />}
      hint={
        !allRequiredDone
          ? intl.formatMessage({ id: 'biographicalEditor.blockIndex.progressHint' })
          : undefined
      }
      onClose={() => history.push(`/${lang}/lines/${idLine}/${idName}`)}
      onGenerate={handleGenerate}
    />
    </div>
    </>
  );
};

export default BlockIndexScreen;
